import {
  Avatar,
  Badge,
  Box,
  Button,
  Collapse,
  Container,
  Group,
  List,
  Paper,
  Rating,
  Stack,
  Sx,
  Text as MantineText,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import { ElementType, useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  IconBriefcase,
  IconBuildingCastle,
  IconCalendarTime,
  IconCamera,
  IconCheck,
  IconChevronDown,
  IconDeviceSpeaker,
  IconDiamond,
  IconFlower,
  IconHeadphones,
  IconHeartHandshake,
  IconMicrophone,
  IconPhoto,
  IconQuote,
  IconStars,
  IconToolsKitchen,
  IconWand,
} from '@tabler/icons';
// eslint-disable-next-line import/no-unresolved
import { A11y, Navigation, Pagination } from 'swiper/modules';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { LicenceS } from '../../../Service/helper/LicenceService';
import { useUpdateBillingPlans } from '../../../Service/hooks/useUpdateBillingPlans';
import { RoutingS } from '../../../Service/RoutingS';
import { BillingInterval } from '../../../Types/Billing';
import BookitupSegmentedControl from '../../../Atoms/BookitupSegmentedControl';
import { DataS } from '../../../Service/DataS';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { useBookitupSubscription } from '../../../Service/Context/BookitupSubscriptionContext';
import { ChargebeeS } from '../../../Service/ChargebeeS';
import { ThemeText } from '../../../Atoms/ThemeText';
import PanePaper from '../../../Molecules/PanePaper';
import AvailableAddons from './AvailableAddons';
import AvailablePlans from './AvailablePlans';
import SubscriptionPane from './SubscriptionPane';
import TrialRedeemAlert from './TrialRedeemAlert';
import { sebastianAvatarUrl } from './ThankYou';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/navigation';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';

const getServiceBadgeLabel = (value: string) =>
  DataS.servicesOptions.find((option) => option.value === value)?.label ?? value;

const BlondeWomanIcon = ({ size = 13 }: { size?: number }) => (
  <span role="img" aria-label="Blonde Frau" style={{ fontSize: size, lineHeight: 1 }}>
    👱‍♀️
  </span>
);

const MusicalKeyboardIcon = ({ size = 13 }: { size?: number }) => (
  <span role="img" aria-label="Musical Keyboard" style={{ fontSize: size, lineHeight: 1 }}>
    🎹
  </span>
);

const packageServiceBadges: { label: string; icon: ElementType; color: string; lightTextShade?: number }[] = [
  { label: getServiceBadgeLabel('DJ'), icon: IconHeadphones, color: 'grape' },
  { label: getServiceBadgeLabel('Fotograf:in'), icon: IconCamera, color: 'pink' },
  { label: getServiceBadgeLabel('Fotobox-Verleih'), icon: IconPhoto, color: 'teal' },
  { label: getServiceBadgeLabel('Catering'), icon: IconToolsKitchen, color: 'orange' },
  { label: getServiceBadgeLabel('Eventtechnik'), icon: IconDeviceSpeaker, color: 'cyan' },
  { label: getServiceBadgeLabel('Agentur'), icon: IconBriefcase, color: 'gray' },
  { label: 'Event-Location', icon: IconBuildingCastle, color: 'violet' },
  { label: 'Freie Redner', icon: IconMicrophone, color: 'red' },
  { label: 'Zauberer', icon: IconWand, color: 'indigo' },
  { label: 'Musiker', icon: MusicalKeyboardIcon, color: 'blue', lightTextShade: 3 },
  { label: 'Floristik', icon: IconFlower, color: 'lime' },
  { label: 'Hochzeitsplaner', icon: IconDiamond, color: 'yellow' },
  { label: 'Make-Up & Hair', icon: BlondeWomanIcon, color: 'blue', lightTextShade: 3 },
];

const packageFounderStoryParagraphs = [
  'Nach meinem Informatik-Studium 2016 habe ich meinen Softwareentwickler-Job gekündigt, um dann mein bisheriges Hobby als Hochzeits- und Event-DJ zum Beruf zu machen.',
  'Zuverlässigkeit und schnelles reagieren bei Anfragen und rechtzeitiges Nachfassen ist in dem Job besonders wichtig, doch mein "Büro" bestand aus Word, der Kalender-App, meinem Mail-Programm und einem Rechnungstool - Automationen waren schlicht nicht möglich und ich musste sehr aufpassen, dass ich nichts durcheinander bringe oder vergesse. Genau deshalb habe ich anfangen mir mein eigenes Tool zu bauen.',
  'Ab 2018 haben dann auch andere mit bookitup gestartet - und ich konnte 2020 die bookitup GmbH gründen und erste Mitarbeiter eingestellen. Seitdem verbessern wir bookitup jeden einzelnen Tag um eine Software zu bauen, die einem wirklich jeden Tag unterstützt.',
];

const PackageFounderStory = ({ sm }: { sm: boolean }) => {
  const [expanded, setExpanded] = useState(false);
  const storyContentId = 'package-founder-story-content';

  return (
    <Paper
      component="section"
      withBorder
      radius="md"
      p={sm ? 'sm' : 'md'}
      mt="lg"
      aria-labelledby="package-founder-story-heading"
      sx={(theme) => ({
        borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2],
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
      })}
    >
      <Group
        position="apart"
        align="center"
        spacing="md"
        noWrap={!sm}
        sx={{
          '@media (max-width: 576px)': {
            alignItems: 'stretch',
            flexDirection: 'column',
          },
        }}
      >
        <Group spacing="sm" noWrap sx={{ minWidth: 0, flex: 1 }}>
          <Avatar
            src={sebastianAvatarUrl}
            alt="Sebastian Mahlke"
            radius="xl"
            size={sm ? 42 : 48}
            color="blue"
            imageProps={{ referrerPolicy: 'no-referrer' }}
            sx={{ flex: '0 0 auto' }}
          >
            <MantineText color="white" weight={800} size="xs">
              SM
            </MantineText>
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <ThemeText
              id="package-founder-story-heading"
              mt={4}
              size={sm ? 'sm' : 'md'}
              weight={900}
              sx={{ lineHeight: 1.25 }}
            >
              Wie bookitup entstanden ist
            </ThemeText>
            <MantineText mt={2} size="xs" sx={{ lineHeight: 1.45 }}>
              und warum Du Dich auf bookitup verlassen kannst.
            </MantineText>
          </Box>
        </Group>

        <Button
          type="button"
          variant="filled"
          color="blue"
          size="xs"
          fullWidth={sm}
          aria-expanded={expanded}
          aria-controls={storyContentId}
          rightIcon={
            <IconChevronDown
              size={15}
              stroke={2.4}
              style={{ transform: expanded ? 'rotate(180deg)' : undefined, transition: 'transform 160ms ease' }}
            />
          }
          onClick={() => setExpanded((current) => !current)}
          sx={{ flex: '0 0 auto' }}
        >
          {expanded ? 'Weniger anzeigen' : 'Story lesen'}
        </Button>
      </Group>

      <Collapse in={expanded}>
        <Box
          id={storyContentId}
          mt="md"
          pt="md"
          sx={(theme) => ({
            borderTop: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
          })}
        >
          <Stack spacing={8}>
            {packageFounderStoryParagraphs.map((paragraph) => (
              <MantineText key={paragraph} size={sm ? 'xs' : 'sm'} sx={{ lineHeight: 1.65 }}>
                {paragraph}
              </MantineText>
            ))}
          </Stack>
          <MantineText mt="sm" size="xs" color="dimmed" weight={800}>
            Sebastian Mahlke - Gründer & Geschäftsführer
          </MantineText>
        </Box>
      </Collapse>
    </Paper>
  );
};

const segmentedControlSx: Sx = (theme) => ({
  width: '100%',
  maxWidth: '100%',
  padding: 3,
  backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.white,
  '.mantine-SegmentedControl-control': {
    minWidth: 0,
  },
  '.mantine-SegmentedControl-label': {
    minHeight: 30,
    minWidth: 0,
    paddingLeft: 8,
    paddingRight: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 1.15,
    whiteSpace: 'normal',
  },
  '.mantine-SegmentedControl-active': {
    boxShadow: theme.colorScheme === 'dark' ? '0 5px 12px rgba(0, 0, 0, 0.28)' : '0 5px 12px rgba(29, 81, 145, 0.12)',
  },
  '@media (max-width: 380px)': {
    padding: 2,
    '.mantine-SegmentedControl-label': {
      minHeight: 28,
      paddingLeft: 5,
      paddingRight: 5,
      fontSize: 11,
    },
  },
});

type CustomerReview = typeof DataS.customerReviews[number];

const getAuthorInitials = (author: string) => {
  const parts = author.trim().split(/\s+/);

  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const orangeThemeIconSx: Sx = (theme) => ({
  color: theme.colorScheme === 'dark' ? theme.colors.orange[2] : theme.colors.orange[9],
  backgroundColor:
    theme.colorScheme === 'dark'
      ? theme.fn.rgba(theme.colors.orange[5], 0.22)
      : theme.fn.rgba(theme.colors.orange[1], 0.82),
});

const customerReviewsCarouselSx: Sx = (theme) => ({
  marginLeft: -4,
  marginRight: -4,
  '.swiper': {
    padding: '4px 28px 38px',
  },
  '.swiper-wrapper': {
    alignItems: 'stretch',
  },
  '.swiper-slide': {
    height: 'auto',
  },
  '.swiper-button-prev, .swiper-button-next': {
    width: 26,
    height: 26,
    marginTop: -32,
    borderRadius: 26,
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.white,
    boxShadow: theme.colorScheme === 'dark' ? '0 3px 10px rgba(0, 0, 0, 0.24)' : '0 3px 10px rgba(29, 81, 145, 0.12)',
    color: '#FE8047',
    '&::after': {
      fontSize: 10,
      fontWeight: 900,
    },
  },
  '.swiper-button-prev': {
    left: 0,
  },
  '.swiper-button-next': {
    right: 0,
  },
  '.swiper-pagination': {
    bottom: 0,
  },
  '.swiper-pagination-bullet': {
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[4],
    opacity: 1,
  },
  '.swiper-pagination-bullet-active': {
    backgroundColor: '#FE8047',
  },
  '@media (min-width: 768px)': {
    marginLeft: -32,
    marginRight: -32,
    '.swiper': {
      paddingLeft: 32,
      paddingRight: 32,
    },
    '.swiper-button-prev': {
      left: 3,
    },
    '.swiper-button-next': {
      right: 3,
    },
  },
});

const CustomerReviewCard = ({ review }: { review: CustomerReview }) => {
  const [expanded, setExpanded] = useState(false);
  const canExpand = review.reviewMessage.length > 420;

  return (
    <Paper
      component="article"
      withBorder
      radius="md"
      p="lg"
      sx={(theme) => ({
        height: '100%',
        display: 'flex',
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
        borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2],
        boxShadow:
          theme.colorScheme === 'dark' ? '0 8px 24px rgba(0, 0, 0, 0.16)' : '0 8px 24px rgba(29, 81, 145, 0.08)',
      })}
    >
      <Stack spacing="md" sx={{ width: '100%' }}>
        <Group spacing="sm" noWrap sx={{ minWidth: 0 }}>
          <Avatar
            src={review.avatarUrl}
            alt=""
            radius="xl"
            size={44}
            color="orange"
            imageProps={{ referrerPolicy: 'no-referrer' }}
          >
            {getAuthorInitials(review.author)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <MantineText weight={800} size="sm" lineClamp={2}>
              {review.author}
            </MantineText>
            <Rating value={5} readOnly size="xs" color="orange" mt={3} aria-label="5 von 5 Sternen" />
          </Box>
        </Group>

        <MantineText
          component="blockquote"
          size="xs"
          italic
          lineClamp={!expanded && canExpand ? 7 : undefined}
          sx={{
            margin: 0,
            lineHeight: 1.65,
            whiteSpace: 'pre-line',
          }}
        >
          {review.reviewMessage}
        </MantineText>

        {canExpand && (
          <UnstyledButton
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
            sx={(theme) => ({
              alignSelf: 'flex-start',
              marginTop: 'auto',
              color: '#FE8047',
              fontSize: 12,
              fontWeight: 800,
              '&:focus-visible': {
                outline: `2px solid ${theme.colors.orange[5]}`,
                outlineOffset: 3,
                borderRadius: 2,
              },
            })}
          >
            {expanded ? 'Weniger anzeigen' : 'Mehr lesen'}
          </UnstyledButton>
        )}
      </Stack>
    </Paper>
  );
};

const CustomerReviewsCarousel = () => {
  const reviews = DataS.customerReviews;

  return (
    <Box component="section" mt="xl" aria-labelledby="customer-reviews-heading">
      <Paper
        withBorder
        radius="lg"
        p="xl"
        sx={(theme) => ({
          overflow: 'hidden',
          borderColor:
            theme.colorScheme === 'dark'
              ? theme.fn.rgba(theme.colors.orange[6], 0.18)
              : theme.fn.rgba(theme.colors.orange[3], 0.34),
          background:
            theme.colorScheme === 'dark'
              ? `linear-gradient(145deg, ${theme.colors.dark[7]} 0%, ${theme.colors.dark[8]} 100%)`
              : 'linear-gradient(145deg, rgba(255, 249, 244, 0.96) 0%, rgba(255, 255, 255, 1) 70%)',
          '@media (max-width: 576px)': {
            padding: 16,
          },
        })}
      >
        <Group
          position="apart"
          align="flex-start"
          spacing="lg"
          mb="lg"
          sx={{
            '@media (max-width: 576px)': {
              flexDirection: 'column',
            },
          }}
        >
          <Group align="flex-start" spacing="md" noWrap>
            <ThemeIcon size={42} radius="md" variant="light" color="orange" sx={orangeThemeIconSx}>
              <IconQuote size={22} stroke={2.4} />
            </ThemeIcon>
            <Box>
              <MantineText size={10} weight={900} transform="uppercase" sx={{ color: '#FE8047', letterSpacing: 0.8 }}>
                Das sagen andere Kunden.
              </MantineText>
              <ThemeText id="customer-reviews-heading" mt={3} size={22} weight={900}>
                Was sich mit bookitup verändert
              </ThemeText>
              <MantineText mt={5} size="sm" color="dimmed">
                Ein System aus der Praxis und ein persönlicher Support, der immer für einen da ist.
              </MantineText>
            </Box>
          </Group>
        </Group>

        <Box sx={customerReviewsCarouselSx}>
          <Swiper
            modules={[A11y, Navigation, Pagination]}
            navigation
            pagination={{ clickable: true, dynamicBullets: reviews.length > 5 }}
            slidesPerView={1.05}
            spaceBetween={16}
            grabCursor
            breakpoints={{
              600: {
                slidesPerView: 2,
                spaceBetween: 16,
              },
              1000: {
                slidesPerView: 3,
                spaceBetween: 20,
              },
            }}
            a11y={{
              prevSlideMessage: 'Vorherige Kundenstimme',
              nextSlideMessage: 'Nächste Kundenstimme',
              paginationBulletMessage: 'Gehe zu Kundenstimme {{index}}',
            }}
          >
            {reviews.map((review) => (
              <SwiperSlide key={`${review.author}-${review.reviewMessage.slice(0, 24)}`}>
                <CustomerReviewCard review={review} />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Paper>
    </Box>
  );
};

const PackageSection = () => {
  const jwtClaims = useJwtClaims();
  const { subscription, refresh: refreshSubscription } = useBookitupSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('Yearly');
  const checkoutRequestHandled = useRef(false);
  const { sm } = useContext(WindowContext);
  const { licence } = jwtClaims;
  const freeLicence = LicenceS.isFreeLicence(licence);
  const hasActiveSubscription = subscription?.status === 'ACTIVE';
  const billingIntervalOptions = DataS.billingIntervals.map((option) => {
    if (option.value !== 'Yearly' || hasActiveSubscription) {
      return option;
    }

    return {
      ...option,
      label: (
        <Group spacing={5} noWrap>
          <MantineText span size={12} weight={800} sx={{ color: 'inherit', lineHeight: 1 }}>
            {option.label}
          </MantineText>
          <MantineText span size={10} weight={900} sx={{ color: '#FE8047', lineHeight: 1 }}>
            -15%
          </MantineText>
        </Group>
      ),
    };
  });

  useUpdateBillingPlans(freeLicence);

  useEffect(() => {
    RoutingS.changeTitle('Paket');
  }, []);

  useEffect(() => {
    if (checkoutRequestHandled.current || searchParams.get('kind') !== 'addon') return;

    const itemId = searchParams.get('itemId')?.trim();
    const quantity = Number(searchParams.get('quantity'));

    if (!itemId || !Number.isInteger(quantity) || quantity < 1) return;

    checkoutRequestHandled.current = true;
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('kind');
    nextSearchParams.delete('itemId');
    nextSearchParams.delete('quantity');
    setSearchParams(nextSearchParams, { replace: true });

    ChargebeeS.openHostedAddonCheckout(itemId, quantity, refreshSubscription);
    // The checkout request is intentionally parsed only when the section mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (subscription) {
      if (subscription.interval !== billingInterval) {
        setBillingInterval(subscription.interval);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription]);

  return (
    <Container p={0}>
      <SubscriptionPane />
      <PanePaper>
        <Box sx={{ minWidth: 0 }}>
          <ThemeText size={sm ? 21 : 28} weight={900} sx={{ maxWidth: 800, lineHeight: 1.2 }}>
            bookitup für Event- & Hochzeitsprofis
          </ThemeText>
          <MantineText
            color="dimmed"
            mt="sm"
            mb="xl"
            size={sm ? 'xs' : 'sm'}
            sx={{ maxWidth: 800, lineHeight: 1.65, overflowWrap: 'break-word' }}
          >
            Behalte den Überblick und automatisiere deinen Buchungsprozess, sodass bookitup alle 'Fäden zusammenhält',
            statt Du.
          </MantineText>

          <ThemeText mt="xl" mb="xs" size="sm" weight={900}>
            Damit du einfach startest und langfristig happy bist:
          </ThemeText>
          <List mb="xl" spacing={5} size={sm ? 'xs' : 'sm'} icon={<IconCheck size={13} stroke={3} color="#FDAF47" />}>
            <List.Item>Kostenlose Video-Akademie für deinen einfachen Einstieg</List.Item>
            <List.Item>Persönlicher E-Mail- und Videocall-Support</List.Item>
            <List.Item>Stetige Weiterentwicklung aus deinem Kundenfeedback</List.Item>{' '}
            <List.Item>Daten-Import aus deinem vorherigen CRM</List.Item>
            <List.Item>Alle Daten sicher und DSGVO-konform gespeichert in 🇩🇪</List.Item>
          </List>
          <Group mt="sm" spacing={6} sx={{ rowGap: 6 }}>
            {packageServiceBadges.map((badge) => {
              const ServiceIcon = badge.icon;

              return (
                <Badge
                  key={badge.label}
                  size="sm"
                  radius="sm"
                  color={badge.color}
                  variant="light"
                  sx={(theme) => ({
                    height: 24,
                    paddingLeft: 8,
                    paddingRight: 9,
                    textTransform: 'none',
                    backgroundColor:
                      theme.colorScheme === 'light' && badge.color === 'blue'
                        ? theme.fn.rgba(theme.colors.blue[1], 0.12)
                        : undefined,
                    color:
                      theme.colorScheme === 'dark'
                        ? theme.colors[badge.color][2]
                        : theme.colors[badge.color][badge.lightTextShade ?? 8],
                  })}
                >
                  <Group spacing={4} noWrap>
                    <ServiceIcon size={13} stroke={2.2} />
                    <MantineText span size={11} weight={700} sx={{ lineHeight: 1 }}>
                      {badge.label}
                    </MantineText>
                  </Group>
                </Badge>
              );
            })}
          </Group>
        </Box>
        <PackageFounderStory sm={sm} />
      </PanePaper>

      <Box component="section" mt={sm ? 21 : 32} aria-labelledby="plans-heading">
        <Stack spacing={6} align="center" mb="lg">
          <MantineText size={10} weight={900} transform="uppercase" sx={{ color: '#FE8047', letterSpacing: 0.8 }}>
            Mehr Fokus für das was wirklich Geld bringt.
          </MantineText>
          <ThemeText id="plans-heading" size={sm ? 23 : 30} weight={900} align="center">
            Welcher Plan passt zu Dir?
          </ThemeText>
          <MantineText color="dimmed" size="sm" align="center" sx={{ maxWidth: 590, lineHeight: 1.6 }}>
            Starte schlank oder automatisiere dein gesamtes Eventgeschäft. Wenn du wächst, wächst bookitup einfach mit.
          </MantineText>
        </Stack>

        <Stack spacing="xs" align="center" mb="xl">
          <Paper
            withBorder
            radius="lg"
            p={5}
            sx={(theme) => ({
              width: '100%',
              maxWidth: 340,
              borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2],
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
            })}
          >
            <BookitupSegmentedControl
              size="xs"
              value={billingInterval}
              data={billingIntervalOptions}
              onChange={(val: BillingInterval) => val && setBillingInterval(val)}
              sx={segmentedControlSx}
            />
          </Paper>
          <MantineText size={10} color="dimmed" align="center">
            {billingInterval === 'Yearly'
              ? 'Clever gewählt: niedrigere Monatspreise bei jährlicher Abrechnung.'
              : 'Maximal flexibel mit monatlicher Abrechnung.'}
          </MantineText>
        </Stack>

        <AvailablePlans billingInterval={billingInterval} />
      </Box>

      <Box component="section" mt={sm ? 48 : 72} aria-labelledby="addons-heading">
        <Stack spacing={6} align="center" mb="xl">
          <ThemeIcon size={44} radius="md" color="orange" variant="light" sx={orangeThemeIconSx}>
            <IconStars size={23} stroke={2.4} />
          </ThemeIcon>
          <MantineText size={10} weight={900} transform="uppercase" sx={{ color: '#FE8047', letterSpacing: 0.8 }}>
            Damit wirklich alles an einem Ort ist.
          </MantineText>
          <ThemeText id="addons-heading" size={sm ? 22 : 28} weight={900} align="center">
            Erweiterungen, die sich perfekt integrieren.
          </ThemeText>
          <MantineText color="dimmed" size="sm" align="center" sx={{ maxWidth: 560, lineHeight: 1.6 }}>
            Damit du nur genau das zahlst, wovon Du wirklich jeden Tag einen Mehrwert hast.
          </MantineText>
        </Stack>
        <AvailableAddons billingInterval={billingInterval} />
      </Box>

      <CustomerReviewsCarousel />
      <Paper
        withBorder
        radius="md"
        p="md"
        mt="sm"
        sx={(theme) => ({
          borderColor: theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2],
          backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
        })}
      >
        <Group position="center" spacing="xl" sx={{ rowGap: 8 }}>
          {['Persönlicher Support', 'Video-Akademie inklusive', 'Sicher in Deutschland gehostet'].map((benefit) => (
            <Group key={benefit} spacing={6} noWrap>
              <IconCheck size={14} stroke={3} color="#FE8047" />
              <MantineText size="xs" weight={700}>
                {benefit}
              </MantineText>
            </Group>
          ))}
        </Group>
      </Paper>

      <Paper
        component="section"
        withBorder
        radius="lg"
        p={sm ? 'lg' : 30}
        mt={sm ? 31 : 32}
        sx={(theme) => ({
          overflow: 'hidden',
          borderColor:
            theme.colorScheme === 'dark'
              ? theme.fn.rgba(theme.colors.blue[5], 0.22)
              : theme.fn.rgba(theme.colors.blue[3], 0.35),
          background:
            theme.colorScheme === 'dark'
              ? `linear-gradient(135deg, ${theme.colors.dark[7]} 0%, ${theme.colors.dark[8]} 100%)`
              : 'linear-gradient(135deg, rgba(239, 246, 255, 1) 0%, rgba(255, 248, 244, 1) 100%)',
        })}
      >
        <Group
          position="apart"
          spacing="xl"
          noWrap={!sm}
          sx={{
            '@media (max-width: 576px)': {
              alignItems: 'stretch',
              flexDirection: 'column',
            },
          }}
        >
          <Group align="flex-start" spacing="md" noWrap>
            <ThemeIcon size={46} radius="xl" color="orange" variant="light" sx={orangeThemeIconSx}>
              <IconHeartHandshake size={24} stroke={2.4} />
            </ThemeIcon>
            <Box>
              <ThemeText size={sm ? 19 : 23} weight={900}>
                Noch unsicher, was zu dir passt?
              </ThemeText>
              <MantineText mt={6} color="dimmed" size="sm" sx={{ maxWidth: 520, lineHeight: 1.6 }}>
                Lass uns kurz zusammen reinschauen wie du arbeitest und was dein größter Engpass ist – wir empfehlen dir
                ehrlich den Plan, der für dich aktuell Sinn macht.
              </MantineText>
            </Box>
          </Group>
          <Button
            size="md"
            color="orange"
            variant="gradient"
            gradient={{ from: '#FE8047', to: '#F75D77', deg: 105 }}
            leftIcon={<IconCalendarTime size={18} />}
            onClick={() => RoutingS.openInNewTab('https://hey.bookitup.de/meetings/sebastian-mahlke/technik-support')}
            sx={{ flex: '0 0 auto' }}
          >
            Persönlich beraten lassen
          </Button>
        </Group>
      </Paper>

      <Stack sx={{ flex: sm ? '1 1 auto' : '0 0 360px', maxWidth: sm ? '100%' : 450, width: '100%' }}>
        {LicenceS.trialRedeemable(jwtClaims) && <TrialRedeemAlert />}
      </Stack>
    </Container>
  );
};

export default PackageSection;
